import { useRef } from "react";
import { Choice } from "@fidget/common/components/form/tripleDatePicker/tripleDatePicker";
import { isAfter } from "date-fns";
import { DatePickerRef } from "../../settings/users/components/adviserEditScreen/tripleDatePickerWithLabel/tripleDatePickerWithLabel";
import { ExistingAdviserForm } from "../../settings/users/types";

type ValidityRangeFields = "from" | "to";

export const useTimeValidityController = (adviser: ExistingAdviserForm) => {
  const fromInputRef = useRef<DatePickerRef>();
  const toInputRef = useRef<DatePickerRef>();

  const resetTimeValidity = () => {
    fromInputRef.current?.reset();
    toInputRef.current?.reset();
  };

  const convertChoiceToDate = (choice: Choice) => {
    return new Date(`${choice.year}-${choice.month}-${choice.day}`);
  };

  const convertUnixDateToChoice = (unixDate?: number): Choice | undefined => {
    if (unixDate !== undefined) {
      const date = new Date(unixDate);

      return {
        day: date.getDate(), // js Date is not so understandable, getDay is day of the week
        year: date.getFullYear(),
        month: date.getMonth() + 1 // js Date.month starts on 0,
      };
    }

    return undefined;
  };

  const validateBottomRange = (choice: Choice) => {
    if (adviser.$.validTo.$) {
      const result = isAfter(adviser.$.validTo.$, convertChoiceToDate(choice));
      return result;
    }

    return true;
  };

  const validateUpperRange = (choice: Choice) => {
    if (adviser.$.validFrom.$) {
      const result = isAfter(convertChoiceToDate(choice), adviser.$.validFrom.$);
      return result;
    }

    return true;
  };

  const handleDatePick = (field: ValidityRangeFields) => (choice: Choice) => {
    field === "to"
      ? adviser.$.validTo.reset(convertChoiceToDate(choice).valueOf())
      : adviser.$.validFrom.reset(convertChoiceToDate(choice).valueOf());
  };

  const handleFieldClear = (field: ValidityRangeFields) => () => {
    if (field === "to") {
      adviser.$.validTo.$ = undefined;
      adviser.$.validTo.onChange(undefined);
      toInputRef.current?.reset();
    }

    if (field === "from") {
      adviser.$.validFrom.$ = undefined;
      adviser.$.validFrom.onChange(undefined);
      fromInputRef.current?.reset();
    }
  };

  const datum = adviser.$.validFrom.$ ? new Date(adviser.$.validFrom.$) : new Date();

  return {
    datum,
    toInputRef,
    fromInputRef,
    handleDatePick,
    handleFieldClear,
    resetTimeValidity,
    validateUpperRange,
    validateBottomRange,
    convertUnixDateToChoice
  };
};
